"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  number: "01",
  title: "Technical & Content Audit",
  subtitle: "Uncovering Bottlenecks",
  description: "I personally audit your technical foundation, crawl errors, site architecture, and content gaps to find exact growth opportunities.",
  icon: Search,
  deliverable: "Comprehensive Audit & Action Plan"
}, {
  number: "02",
  title: "Strategy & On-Page SEO",
  subtitle: "High-Intent Keyword Mapping",
  description: "I map target keywords to landing pages and optimize headers, titles, internal links, and content relevance for maximum search impact.",
  icon: FileText,
  deliverable: "Targeted SEO Strategy Roadmap"
}, {
  number: "03",
  title: "Citations & Local Signals",
  subtitle: "Establishing Authority",
  description: "I audit and build high-grade business citations and local authority signals to solidify your business credibility across search engines.",
  icon: MapPin,
  deliverable: "Verified Citation Placement Log"
}, {
  number: "04",
  title: "Reviews & Trust Strategy",
  subtitle: "Conversion & CTR Signals",
  description: "I implement reputation workflows and Google Business Profile optimizations that drive higher click-through rates and real user conversions.",
  icon: Star,
  deliverable: "Reputation Strategy Playbook"
}, {
  number: "05",
  title: "Schema & Structured Data",
  subtitle: "AI & Entity Recognition",
  description: "I write and deploy custom JSON-LD schema markup so Google and modern AI search engines accurately index and feature your brand.",
  icon: Code2,
  deliverable: "Custom Validated JSON-LD Schema"
}, {
  number: "06",
  title: "Transparent Reporting",
  subtitle: "Direct Expert Accountability",
  description: "You receive clear monthly reports tracking keyword rankings, organic traffic growth, and conversion outcomes—with direct call walkthroughs.",
  icon: BarChart3,
  deliverable: "Monthly Performance Dashboard"
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".step-card", {
        y: 35
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".step-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyprocess" data-nav="light" className="relative py-20 bg-background text-foreground overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1">
            Hands-On Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 text-foreground font-default">
            My Step-by-Step SEO Methodology
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            I do not delegate your campaign to junior account managers. When you hire YE Consultancy, you work directly with me through this structured, transparent search optimization process.
          </p>
        </div>

        {/* Process Steps Grid */}
        <div className="relative">
          {/* Subtle connecting guideline behind cards */}
          <div aria-hidden="true" className="hidden lg:block absolute top-12 left-12 right-12 h-1 bg-accent/30 z-0 rounded-full" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {steps.map((step, i) => {
            const IconComponent = step.icon;
            return <Card key={step.number} data-index={i} className="step-card bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 relative flex flex-col justify-between" style={{
              opacity: 0
            }}>
                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      {/* Step Number Badge */}
                      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg shadow-sm font-serif">
                        {step.number}
                      </div>

                      {/* Subtitle / Focus pill */}
                      <div className="flex items-center gap-2 bg-secondary/80 border border-accent/20 px-3 py-1.5 rounded-full text-xs font-medium text-foreground">
                        <IconComponent className="w-3.5 h-3.5 text-accent" />
                        <span>{step.subtitle}</span>
                      </div>
                    </div>

                    <CardTitle className="text-xl font-bold text-foreground font-default">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pb-6 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {step.description}
                    </CardDescription>

                    <div className="pt-3 border-t border-border/60 flex items-center gap-2 text-xs text-foreground font-medium">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                      <span>{step.deliverable}</span>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Bottom Consultation CTA banner */}
        <div className="mt-16 bg-muted/60 border border-border rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-xl md:text-2xl font-bold text-foreground font-default">
              Ready for a custom SEO strategy that delivers real search growth?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              Book a direct consultation with me to discuss your current rankings, organic goals, and roadmap.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent/10">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}